/**
 * Route Guards for B2B Portal
 * Enforces access control based on user type and permissions
 */

import { useAuthStore } from '@/stores/auth'

// Define access rules for each route
export const routeAccessRules = {
  // Root/Dashboard - everyone can access (dynamic routing handles appropriate dashboard)
  '/': {
    allowedUserTypes: ['internal', 'admin', 'b2b'],
    requiresAuth: true
  },
  '/dashboard': {
    allowedUserTypes: ['internal', 'admin', 'b2b'],
    requiresAuth: true
  },
  
  // Client management - internal only
  '/clients': {
    allowedUserTypes: ['internal', 'admin'],
    requiresAuth: true,
    requiresPermission: 'b2b_clients.view'
  },
  
  // Sales order entry - internal only
  '/sales-order-entry': {
    allowedUserTypes: ['internal', 'admin'],
    requiresAuth: true,
    requiresPermission: 'b2b_orders.create_for_client'
  },
  
  // Order management - internal can see all, B2B sees own
  '/order-management': {
    allowedUserTypes: ['internal', 'admin', 'b2b'],
    requiresAuth: true,
    requiresPermission: 'b2b_orders.view_all',
    b2bFallback: '/orders' // B2B customers redirect to their own orders
  },
  
  // Credit management - internal only
  '/credit-management': {
    allowedUserTypes: ['internal', 'admin'],
    requiresAuth: true,
    requiresPermission: 'b2b_credit.view'
  },
  
  // Invoices - different views for different users
  '/invoices': {
    allowedUserTypes: ['internal', 'admin', 'b2b'],
    requiresAuth: true
  },
  
  // Products - everyone can access
  '/products': {
    allowedUserTypes: ['internal', 'admin', 'b2b'],
    requiresAuth: true
  },
  
  // Pricing - internal only
  '/pricing': {
    allowedUserTypes: ['internal', 'admin'],
    requiresAuth: true,
    requiresPermission: 'b2b_orders.view_all'
  },
  
  // Reports - internal only
  '/reports': {
    allowedUserTypes: ['internal', 'admin'],
    requiresAuth: true,
    requiresPermission: 'b2b_orders.view_all'
  },
  
  // Analytics - internal only
  '/analytics': {
    allowedUserTypes: ['internal', 'admin'],
    requiresAuth: true,
    requiresPermission: 'b2b_analytics.view'
  },
  
  // Team management - managers and admins only
  '/team': {
    allowedUserTypes: ['internal', 'admin'],
    requiresAuth: true,
    requiresRole: ['admin', 'manager'],
    requiresPermission: 'b2b_teams.manage'
  },

  // User management - internal users only
  '/users': {
    allowedUserTypes: ['internal', 'admin'],
    requiresAuth: true,
    requiresPermission: 'b2b_teams.manage'
  },
  
  // Settings - admins only
  '/settings': {
    allowedUserTypes: ['internal', 'admin'],
    requiresAuth: true,
    requiresRole: ['admin']
  },
  
  // B2B Customer specific routes
  '/orders': {
    allowedUserTypes: ['b2b'],
    requiresAuth: true
  },
  
  '/cart': {
    allowedUserTypes: ['internal', 'admin', 'b2b'],
    requiresAuth: true
  },
  
  '/quick-order': {
    allowedUserTypes: ['b2b'],
    requiresAuth: true
  },
  
  '/templates': {
    allowedUserTypes: ['b2b'],
    requiresAuth: true
  },
  
  '/credit': {
    allowedUserTypes: ['b2b'],
    requiresAuth: true
  },
  
  '/company/team': {
    allowedUserTypes: ['b2b'],
    requiresAuth: true,
    requiresRole: ['owner', 'manager']
  },
  
  '/company/addresses': {
    allowedUserTypes: ['b2b'],
    requiresAuth: true,
    requiresRole: ['owner', 'manager']
  },
  
  '/company/profile': {
    allowedUserTypes: ['b2b'],
    requiresAuth: true,
    requiresRole: ['owner', 'manager']
  }
}

/**
 * Main navigation guard
 */
export function setupNavigationGuards(router) {
  router.beforeEach((to, from, next) => {
    const authStore = useAuthStore()
    
    // Initialize auth if not already done
    if (!authStore.user && (localStorage.getItem('b2b_token') || localStorage.getItem('token'))) {
      authStore.initializeAuth()
    }
    
    // Always allow login page
    if (to.path === '/login') {
      if (authStore.isAuthenticated) {
        return next('/')
      }
      return next()
    }
    
    // Check authentication
    if (!authStore.isAuthenticated) {
      return next('/login')
    }
    
    // Get access rules for this route
    const rules = routeAccessRules[to.path]
    if (!rules) {
      // No rules defined - allow access (for now)
      console.warn(`No access rules defined for route: ${to.path}`)
      return next()
    }
    
    // Check user type (normalize to lowercase for consistency)
    const userType = authStore.user?.clientType?.toLowerCase()
    if (!rules.allowedUserTypes.includes(userType)) {
      console.error(`Access denied: User type ${userType} not allowed for ${to.path}`)
      
      // If user type is undefined, this likely means authentication failed
      // Redirect to login instead of creating infinite loop
      if (!userType) {
        console.warn('User type is undefined - redirecting to login')
        return next('/login')
      }
      
      // Check for B2B fallback route
      if (userType === 'b2b' && rules.b2bFallback) {
        return next(rules.b2bFallback)
      }
      
      // Redirect to appropriate dashboard for valid but unauthorized users
      return next('/')
    }
    
    // Check role requirements
    if (rules.requiresRole) {
      const userRole = authStore.user?.role
      const allowedRoles = Array.isArray(rules.requiresRole) ? rules.requiresRole : [rules.requiresRole]
      
      if (!allowedRoles.includes(userRole)) {
        console.error(`Access denied: Role ${userRole} not allowed for ${to.path}`)
        return next('/')
      }
    }
    
    // Check permission requirements (for internal users)
    if (rules.requiresPermission && userType === 'internal') {
      // For internal users without granular permissions, check if they have the showForInternalUsers flag
      // This is a temporary measure until proper permissions are implemented
      const hasPermission = authStore.hasPermission(rules.requiresPermission)
      
      if (!hasPermission && authStore.user?.role !== 'admin') {
        console.warn(`Permission check failed for ${rules.requiresPermission}`)
        // For now, allow internal users through
        // TODO: Implement proper permission checking
      }
    }
    
    next()
  })
}

/**
 * Check if user can access a specific route
 */
export function canAccessRoute(path, user, permissions = {}) {
  const rules = routeAccessRules[path]
  if (!rules) return true // No rules = allowed
  
  // Check user type
  const userType = user?.clientType
  if (!rules.allowedUserTypes.includes(userType)) {
    return false
  }
  
  // Check role
  if (rules.requiresRole) {
    const allowedRoles = Array.isArray(rules.requiresRole) ? rules.requiresRole : [rules.requiresRole]
    if (!allowedRoles.includes(user?.role)) {
      return false
    }
  }
  
  // Check permissions (lenient for now)
  if (rules.requiresPermission && userType === 'internal') {
    if (user?.role === 'admin') return true
    // For other internal users, we're being permissive for now
    return true
  }
  
  return true
}